import {
  findPharmacyLicense,
  isLicenseUnexpired,
  normalizeStateCode,
  todayIsoDate,
} from "./licenseRepository";
import {
  GatingFailure,
  GatingResult,
  RxOrderGatingInput,
} from "./types";

function pharmacyGate(input: RxOrderGatingInput): GatingFailure[] {
  const failures: GatingFailure[] = [];
  const state = normalizeStateCode(input.shipToState);
  const asOf = input.asOfDate || todayIsoDate();
  const pharmacyName = input.pharmacyDisplayName || "The pharmacy";

  if (!state) {
    failures.push({
      gate: "pharmacy",
      code: "PHARMACY_LICENSE_MISSING",
      message: "Ship-to state is required to verify pharmacy shipping authority.",
    });
    return failures;
  }

  const license = findPharmacyLicense(
    input.pharmacyId,
    state,
    input.pharmacyLicenses
  );

  if (!license) {
    failures.push({
      gate: "pharmacy",
      code: "PHARMACY_LICENSE_MISSING",
      state,
      message: `${pharmacyName} does not hold an active nonresident pharmacy license for ${state}. Orders cannot be shipped to this state.`,
    });
    return failures;
  }

  if (license.status !== "active") {
    failures.push({
      gate: "pharmacy",
      code: "PHARMACY_LICENSE_INACTIVE",
      state,
      message: `${pharmacyName}'s ${state} pharmacy license is ${license.status} and cannot be used to ship prescriptions.`,
    });
    return failures;
  }

  if (!isLicenseUnexpired(license.expiration_date, asOf)) {
    failures.push({
      gate: "pharmacy",
      code: "PHARMACY_LICENSE_EXPIRED",
      state,
      message: `${pharmacyName}'s ${state} pharmacy license expired on ${license.expiration_date}.`,
    });
    return failures;
  }

  if (input.isSterileCompound && !license.sterile_authorized) {
    failures.push({
      gate: "pharmacy",
      code: "PHARMACY_STERILE_NOT_AUTHORIZED",
      state,
      message: `${pharmacyName} is not authorized to ship sterile compounded medications into ${state}.`,
    });
  }

  // if (input.shipToPractice && !license.office_use_permitted) {
  //   failures.push({
  //     gate: "pharmacy",
  //     code: "PHARMACY_OFFICE_USE_NOT_PERMITTED",
  //     state,
  //     message: `Shipping to a practice for in-office administration is not permitted in ${state} under ${pharmacyName}'s current license.`,
  //   });
  // }

  return failures;
}

/**
 * Product gate slot — empty for the current catalog (no controlled substances /
 * state-restricted products). Future rules append failures here.
 */
function productGate(_input: RxOrderGatingInput): GatingFailure[] {
  return [];
}

export type RxGatingPipelineOptions = {
  /** Run only these gates (default: pharmacy + product). */
  gates?: Array<"pharmacy" | "product">;
};

/**
 * Additive rules pipeline: pharmacy (Gate 1) → product slot.
 * Prescriber attestation is handled in the Rx wizard UI, not here.
 */
export function runRxOrderGatingPipeline(
  input: RxOrderGatingInput,
  options: RxGatingPipelineOptions = {}
): GatingResult {
  const selected = new Set(options.gates ?? ["pharmacy", "product"]);
  const failures: GatingFailure[] = [];

  if (selected.has("pharmacy")) {
    failures.push(...pharmacyGate(input));
  }
  if (selected.has("product")) {
    failures.push(...productGate(input));
  }

  return {
    passed: failures.length === 0,
    failures,
  };
}

/** Convenience: Gate 1 only (ship-to / pharmacy). */
export function runPharmacyShipToGate(input: RxOrderGatingInput): GatingResult {
  return runRxOrderGatingPipeline(input, { gates: ["pharmacy"] });
}
